import React, { type ReactElement, useCallback, useEffect } from 'react';
import { batch, useDispatch } from 'react-redux';
import { Route, Switch, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Icon,
  Loader,
  ModalIsland,
  TabGroup,
  TabItem,
  Tooltip,
  Typography,
} from '@saviynt/design-system';

import {
  ViolationsEntitlement,
  ViolationsRole,
  ViolationsTab,
  ViolationsTime,
  ViolationsUser,
} from '../../features';
import {
  AAG_SOD_ENTITLEMENT,
  AAG_SOD_ROLE,
  AAG_SOD_TIME,
  AAG_SOD_USER,
  AAG_SOD_VIOLATIONS,
  CF_NEU_BLD,
  CF_SEC_BLD,
  DISABLED_PAGES,
  VIOLATIONS_PAGE_TABS,
  violationsHome,
} from '../../shared/constants';
import {
  useInvertedLocalize,
  useNavigateTo,
  useTypedSelector,
} from '../../shared/hooks';
import { globalActions } from '../../shared/store/actions';
import { testIds } from '../../tests';

import './Violations.css';

/**
 * @typedef {Object} ViolationsProps
 * No props are required for this component
 */

/**
 * Violations component that handles the display and management of violation detection interface.
 * This component includes a header with navigation tabs and a content area for different violation views.
 *
 * @component
 * @description
 * The component manages different views for violations including:
 * - Violations Tab
 * - Time-based violations
 * - User-based violations
 * - Entitlement-based violations
 * - Role-based violations
 *
 * It also handles global loading states and error scenarios with a modal dialog.
 *
 * @example
 * return (
 *   <Violations />
 * )
 *
 * @returns {ReactElement} A React element representing the Violations interface
 */
function Violations(): ReactElement {
  const location = useLocation();
  const dispatch = useDispatch();
  const l = useInvertedLocalize();

  const { navigateToUrl } = useNavigateTo();

  useEffect(() => {
    batch(() => {
      const activeTab = location.pathname || violationsHome;

      dispatch(globalActions.setGlobalError({ state: false, reason: '' }));
      dispatch(globalActions.setViolationsActiveTab(activeTab));
    });
  }, [location, dispatch]);

  const {
    activeViolationsTab,
    isGlobalLoading,
    dataTheme,
    isGlobalError,
    isClassicExpAlertOpen,
  } = useTypedSelector((state) => state.aag.global);

  /**
   * Handles tab change events in the violations interface
   *
   * @param {string} value - The identifier of the selected tab
   * @returns {void}
   */
  const handleViolationsTabChange = useCallback(
    (value: string): void => {
      if (
        !isGlobalLoading &&
        activeViolationsTab !== value &&
        !DISABLED_PAGES.includes(value)
      ) {
        navigateToUrl(value);
      }
    },
    [navigateToUrl, isGlobalLoading, activeViolationsTab]
  );

  /**
   * Handles page refresh when errors occur
   *
   * @returns {void}
   */
  const onRefreshClick = () => {
    window.location.reload();
  };

  /**
   * Navigates back to the request home page
   *
   * @returns {void}
   */
  const onBackClick = () => {
    window.location.replace('/ECMv6/request/requestHome');
  };

  return (
    <div
      data-theme={dataTheme}
      className='Sod-Violations'
      data-testid={testIds.SOD_VIOLATIONS_PAGE}>
      <header
        className='Sod-Violations-header'
        data-testid={testIds.SOD_VIOLATIONS_PAGE_HEADER}>
        {/* Title */}
        <Box className='Sod-Violations-headerTitleWrapper'>
          <Typography kind='h1' color={CF_NEU_BLD}>
            {l('Violations Detection')}
          </Typography>
        </Box>
        {/* TabGroup */}
        <Box className='Sod-Violations-headerTabGroupWrapper'>
          <TabGroup
            value={activeViolationsTab}
            onChange={handleViolationsTabChange}
            className='Sod-Violations-headerTabGroup'>
            {VIOLATIONS_PAGE_TABS.filter((page) => page.isActive).map(
              ({ label, value, icon }) => (
                <TabItem
                  key={value}
                  label={l(label)}
                  value={value}
                  icon={<Icon kind={icon} color={CF_SEC_BLD} />}
                  className={
                    DISABLED_PAGES.includes(value)
                      ? 'Sod-Violations-headerTabItem--isDisabled'
                      : ''
                  }
                />
              )
            )}
          </TabGroup>
        </Box>
        {/* Export Report */}
        {/* IS_READ_ONLY */}
        {/* <Box className='Sod-Violations-exportWrapper'>
          <ButtonSelect
            kind='icon'
            onClick={() => navigateToUrl(AAG_SOD_REPORT)}
            isOpen={false}
            prefixIcon={<Icon kind='Report' />}
            isDisabled={isGlobalLoading || IS_READ_ONLY}
            dataTestId={testIds.SOD_VIOLATIONS_PAGE_HEADER_EXP_BTN}
          />
        </Box> */}
        {/* Classic UI button wrapper */}
        <Box className='Sod-Violations-exportWrapper'>
          <Tooltip
            trigger={
              <Button
                type='button'
                kind='outlined'
                isDisabled={isGlobalLoading}
                onClick={() => {
                  dispatch(globalActions.setIsClassisExperience(true));
                }}
                leftIcon={<Icon kind='Undo' />}>
                {l('Classic Experience')}
              </Button>
            }
            text={l('Return to the Classic Experience')}
            placement='bottom'
          />
        </Box>
      </header>
      <Box className='Sod-Violations-scrollArea'>
        {isGlobalLoading && (
          <Box className='Sod-Violations-loaderWrapper'>
            <Loader kind='loop' format='main' color='brand' />
          </Box>
        )}
        <Switch>
          <Route path={AAG_SOD_VIOLATIONS} component={ViolationsTab} />
          <Route path={AAG_SOD_TIME} component={ViolationsTime} />
          <Route path={AAG_SOD_USER} component={ViolationsUser} />
          <Route path={AAG_SOD_ENTITLEMENT} component={ViolationsEntitlement} />
          <Route path={AAG_SOD_ROLE} component={ViolationsRole} />
        </Switch>
      </Box>
      <ModalIsland
        kind='critical'
        alertTitle={l('Could not Load Page')}
        alertSubtitle={l(
          'This page of the violations did not load properly due to a network issue. Please refresh or try again later.'
        )}
        onClose={() => {}}
        isCloseButtonVisible={false}
        isOpen={isGlobalError}
        primaryButton={
          <Button
            type='button'
            kind='filled'
            isEnabled
            size='medium'
            onClick={onRefreshClick}>
            {l('Refresh')}
          </Button>
        }
        secondaryButton={
          <Button
            type='button'
            kind='ghost'
            size='medium'
            onClick={onBackClick}>
            {l('Go Back')}
          </Button>
        }
        className='Sod-ViolationsErrorModal'
      />
      {/* Modal Island for Return to Classic UI */}
      <ModalIsland
        kind='question'
        alertTitle={l('Return to the Classic Experience?')}
        alertSubtitle={l(
          'You can switch back to the new experience at any time.'
        )}
        onClose={() => dispatch(globalActions.setIsClassisExperience(false))}
        isOpen={isClassicExpAlertOpen}
        primaryButton={
          <Button
            type='button'
            kind='filled'
            size='medium'
            onClick={() => {
              window.location.pathname = '/ECM/soddetail/sodmapped';
            }}>
            {l('Continue')}
          </Button>
        }
        secondaryButton={
          <Button
            type='button'
            kind='ghost'
            size='medium'
            onClick={() => {
              dispatch(globalActions.setIsClassisExperience(false));
            }}>
            {l('Cancel')}
          </Button>
        }
      />
    </div>
  );
}

export default React.memo(Violations);
